import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Generic markdown-style static legal page. Content is hard-coded Polish.
 * Important: This is a TEMPLATE — should be reviewed by a lawyer before
 * production launch (covers minors / educational use).
 */
function LegalPage({ title, children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /><span>Wróć</span>
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display font-black text-4xl mb-2">{title}</h1>
        <div className="text-sm text-[#64748B] mb-8">
          Wersja robocza — wymaga walidacji prawnej przed wdrożeniem produkcyjnym.
        </div>
        <article className="brut bg-white rounded-md p-8 space-y-4 text-[#334155] leading-relaxed">
          {children}
        </article>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalPage title="Regulamin">
      <p><b>§1. Postanowienia ogólne</b></p>
      <p>Niniejszy regulamin określa zasady korzystania z aplikacji „Kartkówki Chemia" (dalej: Aplikacja), służącej do przeprowadzania kartkówek z chemii w szkołach średnich.</p>
      <p><b>§2. Definicje</b></p>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Administrator</b> — Andrzej Downarowicz, podmiot prowadzący Aplikację i administrator danych osobowych w rozumieniu RODO. Kontakt: chemiainfo@kartkowka.pl.</li>
        <li><b>Nauczyciel</b> — pełnoletnia osoba fizyczna posiadająca konto w Aplikacji.</li>
        <li><b>Uczeń</b> — osoba dołączająca do sesji za pomocą kodu, bez konta.</li>
        <li><b>Konto</b> — indywidualne dane identyfikujące Nauczyciela.</li>
      </ul>
      <p><b>§3. Rejestracja</b></p>
      <p>Rejestracja w Aplikacji jest darmowa i otwarta. Nauczyciel zobowiązany jest podać prawdziwy adres e-mail oraz zaakceptować Regulamin i Politykę prywatności.</p>
      <p><b>§4. Korzystanie z Aplikacji</b></p>
      <p>Nauczyciel zobowiązuje się używać Aplikacji wyłącznie do celów edukacyjnych. Zabrania się przesyłania treści naruszających prawo, dobre obyczaje lub prawa osób trzecich.</p>
      <p><b>§5. Dane uczniów</b></p>
      <p>Nauczyciel ponosi odpowiedzialność za uzyskanie odpowiednich zgód rodziców/opiekunów uczniów małoletnich na przetwarzanie ich danych (imię, wyniki kartkówek) w ramach Aplikacji.</p>
      <p><b>§6. Płatności</b></p>
      <p>Aktualnie dostęp do Aplikacji jest darmowy. Administrator zastrzega sobie prawo wprowadzenia planów płatnych w przyszłości — istniejące konta będą o tym poinformowane z 30-dniowym wyprzedzeniem.</p>
      <p><b>§7. Odpowiedzialność</b></p>
      <p>Administrator dokłada starań aby zapewnić ciągłość działania Aplikacji, ale nie odpowiada za przerwy techniczne ani utratę danych spowodowane czynnikami niezależnymi.</p>
      <p><b>§8. Reklamacje i kontakt</b></p>
      <p>Pytania i reklamacje należy kierować na adres e-mail: <a className="underline font-bold" href="mailto:chemiainfo@kartkowka.pl">chemiainfo@kartkowka.pl</a>.</p>
      <p><b>§9. Postanowienia końcowe</b></p>
      <p>Regulamin może być zmieniony — o zmianach Nauczyciele będą informowani e-mailem z 14-dniowym wyprzedzeniem. Dalsze korzystanie z Aplikacji po zmianach oznacza ich akceptację.</p>
      <p className="text-xs text-[#64748B] pt-6 border-t-2 border-dashed border-[#0F172A]">
        Wersja 1.0 · Data: {new Date().toLocaleDateString("pl-PL")}
      </p>
    </LegalPage>
  );
}

export function Privacy() {
  return (
    <LegalPage title="Polityka prywatności">
      <p><b>§1. Administrator danych</b></p>
      <p>Administratorem danych osobowych w rozumieniu RODO (Rozporządzenie Parlamentu Europejskiego i Rady (UE) 2016/679) jest <b>Andrzej Downarowicz</b>. Kontakt w sprawach ochrony danych: <a className="underline font-bold" href="mailto:chemiainfo@kartkowka.pl">chemiainfo@kartkowka.pl</a>.</p>
      <p><b>§2. Zakres zbieranych danych</b></p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Adres e-mail (login, kontakt, weryfikacja konta, reset hasła)</li>
        <li><b>Nazwa wyświetlana</b> — pole tekstowe używane w mailach i podpisie w panelu. <b>Może być pseudonimem</b> (np. „Chemik42"); nie musi zawierać prawdziwego imienia. Zgodnie z RODO art. 25 („privacy by design") zachęcamy do pseudonimizacji.</li>
        <li>Hasło (przechowywane w postaci hash bcrypt — niemożliwe do odczytania)</li>
        <li>Dane techniczne: adres IP, czas logowania (dla bezpieczeństwa)</li>
        <li>Treść utworzonych pytań, klas, sesji (własność intelektualna Nauczyciela)</li>
      </ul>
      <p><b>§3. Cele przetwarzania</b></p>
      <p>Dane są przetwarzane wyłącznie w celu świadczenia usługi (Art. 6 ust. 1 lit. b RODO) oraz dla bezpieczeństwa (Art. 6 ust. 1 lit. f RODO).</p>
      <p><b>§4. Dane uczniów</b></p>
      <p>Aplikacja zbiera od uczniów wyłącznie: <b>imię (lub pseudonim)</b> i <b>odpowiedzi z kartkówki</b>. Dane te są przypisane do sesji konkretnego Nauczyciela. Aplikacja nie zbiera od uczniów: adresów e-mail, numerów telefonów, danych adresowych. Nauczyciel jest administratorem danych uczniów małoletnich i odpowiada za uzyskanie odpowiednich zgód rodziców/opiekunów.</p>
      <p><b>§5. Twoje prawa (RODO)</b></p>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Dostęp i przenoszenie danych</b> (art. 15 i 20) — w panelu <b>Ustawienia → Strefa niebezpieczna → „Pobierz moje dane (ZIP)"</b> możesz w każdej chwili pobrać kompletny eksport swoich danych (profil, klasy, sesje, wyniki, pytania, historia logowań) w formacie JSON + CSV.</li>
        <li><b>Sprostowanie</b> (art. 16) — edycja danych w panelu Ustawienia.</li>
        <li><b>Usunięcie</b> (art. 17, „prawo do bycia zapomnianym") — samoobsługowe kasowanie konta w <b>Ustawienia → Strefa niebezpieczna → „Chcę usunąć swoje konto"</b>. Konto jest wyłączane natychmiast, a dane trwale usuwane po 30 dniach (okres na ewentualne odwołanie decyzji przez kontakt z pomocą).</li>
        <li><b>Sprzeciw i ograniczenie</b> (art. 21 i 18) — kontakt z administratorem.</li>
        <li><b>Skarga do PUODO</b> — masz prawo wnieść skargę do Prezesa Urzędu Ochrony Danych Osobowych (uodo.gov.pl).</li>
      </ul>
      <p><b>§6. Pliki cookies</b></p>
      <p>Aplikacja używa wyłącznie technicznie niezbędnych ciasteczek (sesja zalogowania). Brak ciasteczek marketingowych/analitycznych.</p>
      <p><b>§7. Przekazywanie danych</b></p>
      <p>Dane są przechowywane w bazie MongoDB w infrastrukturze Emergent. Wysyłka e-maili zachodzi przez usługę Resend (USA, certyfikowane standardy bezpieczeństwa).</p>
      <p><b>§8. Okres przechowywania</b></p>
      <p>Dane są przechowywane przez okres aktywności konta. Po samoobsługowym usunięciu konta (art. 17 RODO) dane są w systemie jeszcze przez <b>30 dni</b> jako okno na odwołanie decyzji, po czym zostają <b>trwale skasowane</b> wraz ze wszystkimi klasami, sesjami, wynikami i pytaniami autorskimi (wyjątek: wpisy audit-log dotyczące bezpieczeństwa, np. logowania — do 3 lat dla celów dowodowych, zanonimizowane).</p>
      <p className="text-xs text-[#64748B] pt-6 border-t-2 border-dashed border-[#0F172A]">
        Wersja 1.1 · Ostatnia aktualizacja: {new Date().toLocaleDateString("pl-PL")} · Wymaga walidacji prawnej
      </p>
    </LegalPage>
  );
}

export function Instrukcja() {
  // Legacy stub — content moved to /app/frontend/src/pages/Instrukcja.jsx
  // (a dedicated page with TOC, FAQ accordion and decorative SVGs).
  // Imports left here only if someone re-imports Instrukcja from Legal —
  // they get a re-export-style helper that simply forwards to the new page.
  // We don't import the new page here to avoid a circular import; instead
  // the only consumer (App.js) has been updated to import from Instrukcja.jsx.
  return null;
}

